import React, { useState } from "react";
import styled from "styled-components";
import { SSTFApiClient } from "../services/api";

const DebugContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
`;

const DebugTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #495057;
  font-size: 16px;
`;

const DebugButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  margin: 4px 8px 4px 0;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const DebugOutput = styled.pre`
  background: #343a40;
  color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  margin: 8px 0;
`;

const StatusBadge = styled.span<{ status: "success" | "error" | "warning" }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  margin: 0 4px;
  
  ${(props) => {
    switch (props.status) {
      case "success":
        return "background: #d4edda; color: #155724;";
      case "error":
        return "background: #f8d7da; color: #721c24;";
      case "warning":
        return "background: #fff3cd; color: #856404;";
      default:
        return "background: #e2e3e5; color: #383d41;";
    }
  }}
`;

interface DebugPanelProps {
  apiClient: SSTFApiClient | null;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({ apiClient }) => {
  const [output, setOutput] = useState<string>("");
  const [testing, setTesting] = useState<boolean>(false);

  const addOutput = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setOutput((prev) => `${prev}[${timestamp}] ${message}\n`);
  };

  const clearOutput = () => {
    setOutput("");
  };

  const testProxyHealth = async () => {
    if (!apiClient) {
      addOutput("❌ APIクライアントが設定されていません");
      return;
    }

    setTesting(true);
    addOutput("🔍 プロキシサーバーのヘルスチェック開始...");

    try {
      const result = await apiClient.testProxyConnection();
      if (result.success) {
        addOutput(`✅ ${result.message}`);
      } else {
        addOutput(`❌ ${result.message}`);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "不明なエラー";
      addOutput(`❌ プロキシヘルスチェック失敗: ${errorMessage}`);
    } finally {
      setTesting(false);
    }
  };

  const testApiConnection = async () => {
    if (!apiClient) {
      addOutput("❌ APIクライアントが設定されていません");
      return;
    }

    setTesting(true);
    addOutput("🔍 SSTF API接続テスト開始...");

    try {
      const workspaces = await apiClient.getWorkspaces();
      addOutput(
        `✅ SSTF API接続成功: ${workspaces.length}個のワークスペースを取得`
      );
      addOutput(
        `ワークスペース一覧: ${workspaces.map((w) => w.name).join(", ")}`
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "不明なエラー";
      addOutput(`❌ SSTF API接続失敗: ${errorMessage}`);
    } finally {
      setTesting(false);
    }
  };

  const testFullConnection = async () => {
    if (!apiClient) {
      addOutput("❌ APIクライアントが設定されていません");
      return;
    }

    setTesting(true);
    addOutput("🔍 完全接続テスト開始...");

    // プロキシテスト
    await testProxyHealth();

    // 少し待機
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // APIテスト
    await testApiConnection();

    addOutput("🏁 完全接続テスト完了");
    setTesting(false);
  };

  const getCurrentStatus = () => {
    if (!apiClient)
      return { status: "error" as const, message: "APIクライアント未設定" };

    const origin =
      typeof window !== "undefined" ? window.location.origin : "unknown";
    return {
      status: "warning" as const,
      message: `設定済み (Origin: ${origin})`,
    };
  };

  const currentStatus = getCurrentStatus();

  return (
    <DebugContainer>
      <DebugTitle>
        🔧 デバッグ・診断パネル
        <StatusBadge status={currentStatus.status}>
          {currentStatus.message}
        </StatusBadge>
      </DebugTitle>

      <div>
        <DebugButton onClick={testProxyHealth} disabled={testing || !apiClient}>
          プロキシヘルスチェック
        </DebugButton>
        <DebugButton
          onClick={testApiConnection}
          disabled={testing || !apiClient}
        >
          API接続テスト
        </DebugButton>
        <DebugButton
          onClick={testFullConnection}
          disabled={testing || !apiClient}
        >
          完全接続テスト
        </DebugButton>
        <DebugButton onClick={clearOutput}>ログクリア</DebugButton>
      </div>

      {output && <DebugOutput>{output}</DebugOutput>}

      {testing && (
        <div style={{ color: "#007bff", fontSize: "14px", marginTop: "8px" }}>
          ⏳ テスト実行中...
        </div>
      )}
    </DebugContainer>
  );
};
